import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Flex, Spinner } from '@chakra-ui/react';

import Card from 'components/card/Card';
import ContactSearchBar from 'components/acturis/ContactSearchBar';
import ContactResultsTable from 'components/acturis/ContactResultsTable';
import ActurisConfigGate from './components/ActurisConfigGate';

import { useUrlFilters } from 'hooks/useUrlFilters';
import { useContactSearch } from 'hooks/useContactSearch';
import { useRecentContacts } from 'hooks/useRecentContacts';
import { useActurisConfigStatus } from 'views/quote_marketplace/hooks';

import { CLIENT_CENTER_LIST_PATH } from './constants';

import type { ActurisContact, ContactSearchType } from 'api/quote-marketplace';

const FILTER_KEYS = ['search_type', 'search'] as const;

const TABLE_HEADERS = ['Client Name', 'Client Address', 'Postal Code'];

const PAGE_SIZE = 10;

const ClientCenterListView = () => {
  const navigate = useNavigate();

  const { setFilters, resetAll, searchParams } = useUrlFilters(FILTER_KEYS);

  const { syncStatus } = useActurisConfigStatus();

  const [searchType, setSearchType] = useState<ContactSearchType>('name');
  const [searchValue, setSearchValue] = useState('');

  const { results, isSearching, triggerSearch, clearSearch, activeSearch } =
    useContactSearch({ enabled: syncStatus === 'ready' });

  const { contacts: recentContacts, isLoading: isLoadingRecent } =
    useRecentContacts({
      enabled: syncStatus === 'ready' && activeSearch.trim().length === 0,
    });

  const hasActiveSearch = activeSearch.trim().length > 0;

  useEffect(() => {
    const urlSearchType = searchParams.get('search_type') as ContactSearchType;
    const urlSearch = searchParams.get('search') || '';
    setSearchType(urlSearchType || 'name');
    setSearchValue(urlSearch);

    if (urlSearch && syncStatus === 'ready') {
      triggerSearch(urlSearch, urlSearchType || 'name', 1, PAGE_SIZE);
    }
  }, [searchParams, syncStatus]);

  const onCustomerClick = (contact: ActurisContact) => {
    navigate(`/admin${CLIENT_CENTER_LIST_PATH}/${contact.contact_ref}`);
  };

  const handleSearch = () => {
    const trimmedSearch = searchValue.trim();
    if (!trimmedSearch) return;

    triggerSearch(trimmedSearch, searchType, 1, PAGE_SIZE);
    setFilters({
      search_type: searchType,
      search: trimmedSearch,
    });
  };

  const handleReset = () => {
    setSearchType('name');
    setSearchValue('');
    clearSearch();
    resetAll();
  };

  const customers = hasActiveSearch ? results : recentContacts;
  const isLoading = hasActiveSearch ? isSearching : isLoadingRecent;

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <ActurisConfigGate>
        <Box px="20px">
          <ContactSearchBar
            searchType={searchType}
            onSearchTypeChange={setSearchType}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
            onSearch={handleSearch}
            onReset={handleReset}
            hasActiveSearch={hasActiveSearch}
          />
        </Box>

        {isLoading ? (
          <Flex justify="center" py="40px">
            <Spinner />
          </Flex>
        ) : (
          <ContactResultsTable
            headers={TABLE_HEADERS}
            contacts={customers}
            onRowClick={onCustomerClick}
          />
        )}

        {!isLoading && customers.length === 0 && (
          <Box textAlign="center" w="100%" py="20px">
            No customers found
          </Box>
        )}
      </ActurisConfigGate>
    </Card>
  );
};

export default ClientCenterListView;
